export interface X1Language {
  readonly Id: number;
  readonly Index: number;
  readonly Name: string;

  readonly AgeCategories: readonly X2AgeCategory[] | null;
}

export interface X2AgeCategory {
  readonly Id: number;

  readonly Language: X1Language | null;
  readonly LanguageId: number;

  readonly Index: number;
  readonly Name: string;

  readonly Levels: readonly X3Level[] | null;
}

export interface X3Level {
  readonly Id: number;

  readonly AgeCategory: X2AgeCategory | null;
  readonly AgeCategoryId: number;

  readonly Index: number;
  readonly Name: string;

  readonly Units: readonly X4Unit[] | null;
}

export interface X4Unit {
  readonly Id: number;

  readonly Level: X3Level | null;
  readonly LevelId: number;

  readonly Index: number;
  readonly Name: string;

  readonly Lessons: readonly X5Lesson[] | null;
}

export const X5LessonTypes = ['Standard', 'Examination', 'Demo'] as const;
export type X5LessonType = typeof X5LessonTypes[number];

export const X5LessonTypeLabels: { readonly [K in X5LessonType]: string } = {
  Standard: 'Standard',
  Examination: 'Examination',
  Demo: 'Demonstration'
};

export interface X5Lesson {
  readonly Id: number;

  readonly UnitId: number;
  readonly Unit: X4Unit | null;

  readonly Index: number;
  readonly LessonType: X5LessonType;
  readonly Name: string;

  readonly Pages: readonly X6Page[] | null;
  readonly Snapshots: readonly X5LessonSnapshot[] | null;
}

export interface X6Page {
  readonly Id: number;

  readonly LessonId: number;
  readonly Lesson: X5Lesson | null;

  readonly Index: number;
  readonly Name: string;

  readonly PushableBlocks: readonly X7PushableBlock[] | null;
}

export interface X7PushableBlock {
  readonly Id: number;

  readonly PageId: number;
  readonly Page: X6Page | null;

  readonly Index: number;
  readonly Name: string;

  readonly Blocks: readonly X8Block[] | null;
}

export const X8BlockTypes = ['Visible', 'Pushable', 'Hidden'] as const;

export type X8BlockType = typeof X8BlockTypes[number];
export interface X8Block {
  readonly Id: number;

  readonly PushableBlockId: number;
  readonly PushableBlock: X7PushableBlock | null;

  readonly Index: number;
  readonly BlockType: X8BlockType;

  readonly Bal: number;
  readonly LessonType: string;

  readonly Items: readonly X9Content[] | null;
}

export const X9ContentTypes = [
  'static-html',
  'static-image',
  'matching-question',
  'select-question',
  'gap-filling',
  'multiple-choice-question',
  'grouping-exercise',
  'flashcards',
  'slideshow',
  'open-ended-question',
  'open-ended-question-inline',
  'synced-audio',
  'synced-video'
] as const;

export type X9ContentType = typeof X9ContentTypes[number];

export interface X9Content {
  readonly Id: number;

  readonly BlockId: number;
  readonly Block: X8Block | null;

  readonly Index: number;
  readonly ContentType: X9ContentType;
  readonly Contents: string;
}

/******************************************************************************/

export interface X5LessonSnapshot {
  readonly Id: number;

  readonly LessonId: number;
  readonly Lesson: X5Lesson | null;

  readonly LessonType: X5LessonType;
  readonly Name: string;

  readonly Active: boolean;
  readonly Version: string;
  readonly CreatedOn: number;

  readonly PageSnapshots: readonly X6PageSnapshot[] | null;
}

export interface X6PageSnapshot {
  readonly Id: number;

  readonly LessonSnapshotId: number;
  readonly LessonSnapshot: X5LessonSnapshot | null;

  readonly Name: string;

  readonly PushableBlockSnapshots: readonly X7PushableBlockSnapshot[] | null;
}

export interface X7PushableBlockSnapshot {
  readonly Id: number;

  readonly PageSnapshotId: number;
  readonly PageSnapshot: X6PageSnapshot | null;

  readonly Name: string;

  readonly BlockSnapshots: readonly X8BlockSnapshot[] | null;
}

export interface X8BlockSnapshot {
  readonly Id: number;

  readonly PushableBlockSnapshotId: number;
  readonly PushableBlockSnapshot: X7PushableBlockSnapshot | null;

  readonly BlockType: X8BlockType;

  readonly Bal: number | null;

  readonly   ContentSnapshots: readonly X9ContentSnapshot[] | null;
}

export interface X9ContentSnapshot {
  readonly Id: number;

  readonly BlockSnapshotId: number;
  readonly BlockSnapshot: X8BlockSnapshot | null;

  readonly ContentType: X9ContentType;
  readonly Contents: string;
}

/******************************************************************************/

export interface X1LanguageUpdateModel {
  Index?: number;
  Name: string;
}

export interface X2AgeCategoryUpdateModel {
  LanguageId: number;
  Index?: number;
  Name: string;
}

export interface X3LevelUpdateModel {
  AgeCategoryId: number;
  Index?: number;
  Name: string;
}


export interface X4UnitUpdateModel {
  LevelId: number;
  Index?: number;
  Name: string;
}

export interface X5LessonUpdateModel {
  UnitId: number;
  Index?: number;

  LessonType: X5LessonType;
  Name: string;
}

export interface X6PageUpdateModel {
  LessonId: number;
  Index?: number;
  Name: string;
}

export interface X7PushableBlockUpdateModel {
  PageId: number;
  Index?: number;
  Name: string;
}

export interface X8BlockUpdateModel {
  PushableBlockId: number;
  Index?: number;
  BlockType: X8BlockType;
  BlockId: number | null;
  Bal: number | null;
}

export interface X9ContentUpdateModel {
  BlockId: number;
  Index?: number;

  ContentType: X9ContentType;
  Contents: string;
}

/******************************************************************************/

export interface ContentTreeDto {
  readonly Languages: readonly X1Language[];
}
